/**
 * Template Management Component
 *
 * List view for managing WhatsApp templates with filters and actions
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Tooltip,
  Grid,
  Typography,
  CircularProgress,
  Menu
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Sync as SyncIcon,
  Visibility as PreviewIcon,
  MoreVert as MoreIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Schedule as PendingIcon,
  Drafts as DraftIcon,
  List as ListIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import {
  WhatsAppTemplate,
  WhatsAppTemplateStatus,
  WhatsAppTemplateCategory,
  TemplateCreateRequest
} from '../../types/whatsapp-templates';
import whatsappTemplatesApi from '../../services/whatsapp-templates-api';
import TemplateBuilder from './TemplateBuilder';

interface TemplateManagementProps {
  accountId: number;
  channelId?: number;
}

const TemplateManagement: React.FC<TemplateManagementProps> = ({ accountId, channelId }) => {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [searchFilter, setSearchFilter] = useState('');

  // UI state
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<WhatsAppTemplate | null>(null);
  const [syncing, setSyncing] = useState(false);

  // Menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTemplate, setMenuTemplate] = useState<WhatsAppTemplate | null>(null);

  // "Names on WhatsApp" dialog
  const [namesDialogOpen, setNamesDialogOpen] = useState(false);
  const [namesOnProvider, setNamesOnProvider] = useState<Array<{ name: string; language: string; status: string }>>([]);
  const [namesLoading, setNamesLoading] = useState(false);
  const [namesError, setNamesError] = useState<string | null>(null);

  // Load templates
  const loadTemplates = async () => {
    setLoading(true);
    setError(null);

    try {
      const filters = {
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        search: searchFilter || undefined
      };

      const response = await whatsappTemplatesApi.listTemplates(accountId, filters);

      if (response.success) {
        setTemplates(response.templates);
      } else {
        setError('Failed to load templates');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [accountId, statusFilter, categoryFilter, searchFilter]);

  // Handlers
  const handleCreateTemplate = async (templateData: TemplateCreateRequest) => {
    const response = await whatsappTemplatesApi.createTemplate(accountId, templateData);

    if (response.success) {
      setBuilderOpen(false);
      setSelectedTemplate(null);
      loadTemplates();
    }
  };

  const handleUpdateTemplate = async (templateData: TemplateCreateRequest) => {
    if (!selectedTemplate) return;

    const response = await whatsappTemplatesApi.updateTemplate(
      selectedTemplate.id,
      templateData
    );

    if (response.success) {
      setBuilderOpen(false);
      setSelectedTemplate(null);
      loadTemplates();
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;

    try {
      await whatsappTemplatesApi.deleteTemplate(templateToDelete.id);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      loadTemplates();
    } catch (err: any) {
      setError(err.message || 'Failed to delete template');
    }
  };

  const handleSubmitForApproval = async (template: WhatsAppTemplate) => {
    if (!channelId) {
      setError('No WhatsApp channel configured');
      return;
    }

    try {
      const response = await whatsappTemplatesApi.submitForApproval(template.id, channelId);

      if (response.success) {
        loadTemplates();
        setError(null);
      } else {
        const msg = response.error || response.message || 'Failed to submit template';
        const isNameInUse = typeof msg === 'string' && /already in use|name.*in use/i.test(msg);
        setError(isNameInUse ? `${msg} Edit the template and choose a different name, then try again.` : msg);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to submit template';
      const isNameInUse = typeof msg === 'string' && /already in use|name.*in use/i.test(msg);
      setError(isNameInUse ? `${msg} Edit the template and choose a different name, then try again.` : msg);
    }
  };

  const handleSyncStatus = async (template: WhatsAppTemplate) => {
    if (!channelId) {
      setError('No WhatsApp channel configured');
      return;
    }

    try {
      const response = await whatsappTemplatesApi.syncTemplateStatus(template.id, channelId);

      if (response.success) {
        loadTemplates();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sync template status');
    }
  };

  const handleSyncAll = async () => {
    if (!channelId) {
      setError('No WhatsApp channel configured');
      return;
    }

    setSyncing(true);
    try {
      const response = await whatsappTemplatesApi.syncAllTemplates(channelId);

      if (response.success) {
        loadTemplates();
      } else {
        setError('Failed to sync templates');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to sync templates');
    } finally {
      setSyncing(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, template: WhatsAppTemplate) => {
    setAnchorEl(event.currentTarget);
    setMenuTemplate(template);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTemplate(null);
  };

  const handleViewNamesOnWhatsApp = async () => {
    if (!channelId) {
      setError('No WhatsApp channel configured');
      return;
    }
    setNamesDialogOpen(true);
    setNamesLoading(true);
    setNamesError(null);
    setNamesOnProvider([]);
    try {
      const res = await whatsappTemplatesApi.getTemplateNamesOnProvider(channelId);
      if (res.success && res.templates) {
        setNamesOnProvider(res.templates);
      } else {
        setNamesError('Could not load template names');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const msg =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail) && detail.length > 0
            ? (detail[0]?.msg ?? JSON.stringify(detail))
            : err?.message || 'Failed to load names';
      setNamesError(msg);
    } finally {
      setNamesLoading(false);
    }
  };

  const handleCopyNames = () => {
    const text = namesOnProvider.map((t) => t.name).join('\n');
    navigator.clipboard.writeText(text);
  };

  // Status badge component
  const StatusBadge: React.FC<{ status: WhatsAppTemplateStatus }> = ({ status }) => {
    const statusConfig = {
      [WhatsAppTemplateStatus.DRAFT]: {
        color: 'default' as const,
        icon: <DraftIcon fontSize="small" />,
        label: 'Draft'
      },
      [WhatsAppTemplateStatus.PENDING]: {
        color: 'warning' as const,
        icon: <PendingIcon fontSize="small" />,
        label: 'Pending'
      },
      [WhatsAppTemplateStatus.APPROVED]: {
        color: 'success' as const,
        icon: <ApprovedIcon fontSize="small" />,
        label: 'Approved'
      },
      [WhatsAppTemplateStatus.REJECTED]: {
        color: 'error' as const,
        icon: <RejectedIcon fontSize="small" />,
        label: 'Rejected'
      },
      [WhatsAppTemplateStatus.DELETED]: {
        color: 'default' as const,
        icon: <DeleteIcon fontSize="small" />,
        label: 'Deleted'
      }
    };

    const config = statusConfig[status];

    return <Chip icon={config.icon} label={config.label} color={config.color} size="small" />;
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">WhatsApp Templates</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<ListIcon />}
            onClick={handleViewNamesOnWhatsApp}
            disabled={!channelId}
          >
            View names on WhatsApp
          </Button>
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleSyncAll}
            disabled={syncing || !channelId}
          >
            Sync All
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedTemplate(null);
              setBuilderOpen(true);
            }}
          >
            Create Template
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search by name or body..."
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="REJECTED">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="UTILITY">Utility</MenuItem>
                <MenuItem value="MARKETING">Marketing</MenuItem>
                <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Templates Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Language</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No templates found. Create your first template to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              templates.map((template) => (
                <TableRow key={template.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {template.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" noWrap>
                      {template.body.substring(0, 50)}
                      {template.body.length > 50 && '...'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={template.category} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{template.language}</TableCell>
                  <TableCell>
                    <StatusBadge status={template.status} />
                  </TableCell>
                  <TableCell>{template.usage_count}</TableCell>
                  <TableCell>
                    {new Date(template.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, template)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuTemplate) {
              setSelectedTemplate(menuTemplate);
              setBuilderOpen(true);
            }
            handleMenuClose();
          }}
          disabled={menuTemplate?.status === WhatsAppTemplateStatus.APPROVED}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuTemplate && menuTemplate.status === WhatsAppTemplateStatus.DRAFT) {
              handleSubmitForApproval(menuTemplate);
            }
            handleMenuClose();
          }}
          disabled={menuTemplate?.status !== WhatsAppTemplateStatus.DRAFT}
        >
          <SendIcon fontSize="small" sx={{ mr: 1 }} />
          Submit for Approval
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuTemplate) {
              handleSyncStatus(menuTemplate);
            }
            handleMenuClose();
          }}
          disabled={menuTemplate?.status === WhatsAppTemplateStatus.DRAFT}
        >
          <SyncIcon fontSize="small" sx={{ mr: 1 }} />
          Sync Status
        </MenuItem>

        <MenuItem
          onClick={() => {
            if (menuTemplate) {
              setTemplateToDelete(menuTemplate);
              setDeleteDialogOpen(true);
            }
            handleMenuClose();
          }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Names on WhatsApp Dialog */}
      <Dialog open={namesDialogOpen} onClose={() => setNamesDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Template names on your WhatsApp account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            These names are already in use. When creating a new template, use a name that is not in this list.
          </Typography>
          {namesLoading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress />
            </Box>
          ) : namesError ? (
            <Alert severity="error">{namesError}</Alert>
          ) : namesOnProvider.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No templates on WhatsApp yet, or could not load. Try &quot;Sync All&quot; first.
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Language</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {namesOnProvider.map((t, i) => (
                      <TableRow key={`${t.name}-${t.language}-${i}`}>
                        <TableCell>{t.name}</TableCell>
                        <TableCell>{t.language}</TableCell>
                        <TableCell>{t.status || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button
                startIcon={<CopyIcon />}
                onClick={handleCopyNames}
                size="small"
                sx={{ mt: 2 }}
              >
                Copy names (one per line)
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNamesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Template Builder Dialog */}
      <Dialog
        open={builderOpen}
        onClose={() => {
          setBuilderOpen(false);
          setSelectedTemplate(null);
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle>
          {selectedTemplate ? 'Edit Template' : 'Create New Template'}
        </DialogTitle>
        <DialogContent>
          <TemplateBuilder
            accountId={accountId}
            channelId={channelId}
            initialTemplate={selectedTemplate || undefined}
            onSave={selectedTemplate ? handleUpdateTemplate : handleCreateTemplate}
            onCancel={() => {
              setBuilderOpen(false);
              setSelectedTemplate(null);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "{templateToDelete?.name}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteTemplate} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TemplateManagement;
